import { create } from 'zustand'
import { battleApi } from '../services/battleApi'
import useAppStore from '../../../store/useAppStore'
import audioService from '../../../services/audioService'

const normalizeBattleCases = (casesData) => {
  if (Array.isArray(casesData)) {
    return casesData
  }

  if (casesData && typeof casesData === 'object') {
    return Object.values(casesData)
  }

  return []
}

const normalizeCategoryOrder = (categoryOrder) => {
  if (!Array.isArray(categoryOrder)) {
    return []
  }

  return categoryOrder.filter(
    (category) => typeof category === 'string' && category.trim() !== ''
  )
}

const useBattleStore = create((set, get) => ({
  // State
  battles: [], // Массив активных баттлов (waiting и finished)
  availableCases: [], // Массив доступных кейсов для создания баттла
  categoryOrder: [],
  currentBattle: null,
  isLoading: false,
  isCreating: false,
  error: null,

  // Actions
  
  /**
   * Загрузить список всех баттлов и доступных кейсов
   * API возвращает battles как объект, конвертируем в массив
   * Также получаем список кейсов для создания баттла
   */
  loadBattles: async () => {
    try {
      set({ isLoading: true, error: null })
      const data = await battleApi.getBattles()
      const normalizedCases = normalizeBattleCases(data.cases)
      const normalizedCategoryOrder = normalizeCategoryOrder(data.categoryOrder)
      
      // data содержит { battles: [], cases: [] }
      set({ 
        battles: data.battles || [],
        availableCases: normalizedCases,
        categoryOrder: normalizedCategoryOrder,
        isLoading: false 
      })
    } catch (error) {
      console.error('Load battles error:', error)
      set({ 
        error: error.message || 'Ошибка загрузки баттлов',
        isLoading: false 
      })
    }
  },

  /**
   * Загрузить информацию о конкретном баттле
   */
  loadBattle: async (battleId) => {
    try {
      set({ isLoading: true, error: null })
      const battle = await battleApi.getBattle(battleId)
      set({ currentBattle: battle, isLoading: false })
    } catch (error) {
      console.error('Load battle error:', error)
      set({ 
        error: error.message || 'Ошибка загрузки баттла',
        isLoading: false 
      })
    }
  },

  /**
   * Создать новый баттл
   * @param {Object} params - Параметры создания баттла
   * @param {string} params.caseId - ID кейса
   * @param {number} params.countPlayers - Количество игроков (2-4)
   * @param {number} params.countCase - Количество кейсов (1-5)
   * @param {string} params.type - Тип определения победителя ("more" | "less" | "random")
   */
  createBattle: async ({ caseId, countPlayers, countCase, type = 'more' }) => {
    try {
      set({ isCreating: true, error: null })
      
      // Вызываем API для создания баттла
      const { battle, user } = await battleApi.createBattle({ 
        caseId, 
        countPlayers, 
        countCase, 
        type 
      })
      
      // Обновляем баланс пользователя в useAppStore
      const appStore = useAppStore.getState()
      if (user && user.balance !== undefined) {
        appStore.setUser({ ...appStore.user, balance: user.balance })
      }
      
      // Добавляем созданный баттл в список battles
      // Преобразуем данные в формат, совместимый со списком
      const newBattle = {
        id: battle.id,
        case: battle.case,
        status: battle.status,
        count_players: battle.count_players,
        count_case: battle.count_case,
        type: battle.type,
        battle_close_in_seconds: battle.battle_close_in_seconds,
        need_balance: battle.need_balance,
        you: 1, // Это наш баттл
        players: [{
          user_id: battle.user_id,
          user_name: battle.user_name,
          user_ava: battle.user_ava,
          slot: battle.slot,
          you: 1
        }]
      }
      
      // Добавляем баттл в начало списка
      const { battles } = get()
      set({ 
        battles: [newBattle, ...battles],
        isCreating: false 
      })
      
      return battle.id
    } catch (error) {
      console.error('Create battle error:', error)
      set({ 
        error: error.message || 'Ошибка создания баттла',
        isCreating: false 
      })
      throw error
    }
  },

  /**
   * Присоединиться к баттлу
   * @param {string} battleId - ID баттла
   * @returns {Object} - { battleId, hasResults, results } где hasResults указывает, был ли это последний игрок
   */
  joinBattle: async (battleId) => {
    try {
      set({ isLoading: true, error: null })
      
      // Вызываем API для присоединения к баттлу
      const { battle, results, user } = await battleApi.joinBattle(battleId)
      
      // Обновляем баланс пользователя в useAppStore
      const appStore = useAppStore.getState()
      if (user && user.balance !== undefined) {
        appStore.setUser({ ...appStore.user, balance: user.balance })
      }
      
      // Обновляем баттл в списке battles
      const { battles } = get()
      const updatedBattles = battles.map(b => {
        if (b.id === battleId) {
          // Обновляем статус и добавляем нового игрока
          return {
            ...b,
            status: battle.status,
            battle_close_in_seconds: battle.battle_close_in_seconds,
            result_casebattle: results, // Сохраняем результаты если есть
            // Добавляем текущего пользователя в список игроков
            players: [
              ...b.players,
              {
                user_id: battle.user_id,
                user_name: battle.user_name,
                user_ava: battle.user_ava,
                slot: battle.slot,
                you: 1 // Это мы присоединились
              }
            ]
          }
        }
        return b
      })
      
      set({ 
        battles: updatedBattles,
        isLoading: false 
      })
      
      // Воспроизводим звук присоединения к баттлу
      audioService.play('userJoinBattle')
      
      // Если это текущий баттл, обновляем и его
      const { currentBattle } = get()
      if (currentBattle && currentBattle.id === battleId) {
        set({
          currentBattle: {
            ...currentBattle,
            status: battle.status,
            battle_close_in_seconds: battle.battle_close_in_seconds,
            result_casebattle: results,
            players: [
              ...currentBattle.players,
              {
                user_id: battle.user_id,
                user_name: battle.user_name,
                user_ava: battle.user_ava,
                slot: battle.slot,
                you: 1
              }
            ]
          }
        })
      }
      
      // Возвращаем информацию о присоединении
      return {
        battleId: battle.id,
        hasResults: !!results, // true если присоединился последний игрок
        results: results, // Массив результатов розыгрыша или null
        status: battle.status // waiting или finished
      }
    } catch (error) {
      console.error('Join battle error:', error)
      set({ 
        error: error.message || 'Ошибка присоединения к баттлу',
        isLoading: false 
      })
      throw error
    }
  },

  /**
   * Обновить данные баттла (вызывается из WebSocket)
   */
  updateBattle: (battleData) => {
    const { currentBattle } = get()
    
    if (currentBattle && currentBattle.id === battleData.id) {
      set({ currentBattle: { ...currentBattle, ...battleData } })
    }

    const { battles } = get()
    const updatedBattles = battles.map(battle => 
      battle.id === battleData.id ? { ...battle, ...battleData } : battle
    )
    set({ battles: updatedBattles })
  },

  /**
   * Добавить новый баттл в список (вызывается из WebSocket)
   */
  addBattle: (battleData) => {
    const { battles } = get()
    const exists = battles.some(b => b.id === battleData.id)
    
    if (!exists) {
      set({ battles: [battleData, ...battles] })
    }
  },

  /**
   * Удалить баттл из списка (вызывается из WebSocket)
   */
  removeBattle: (battleId) => {
    const { battles } = get()
    set({ battles: battles.filter(b => b.id !== battleId) })
  },

  /**
   * Очистить текущий баттл
   */
  clearCurrentBattle: () => {
    set({ currentBattle: null })
  },

  /**
   * Очистить ошибку
   */
  clearError: () => {
    set({ error: null })
  }
}))

export default useBattleStore
